import { textColors, WrapModes } from '@taxdown/piccolo-web';
import { PueblaPreviousYearsResponse, SatResponseType } from 'Services/sat/types';
import { RequestPreviousYearResponse } from 'Services/previousYears/types';
import { history } from 'Store';
import {
  PreviousYearSatStatus,
  PreviousYearsResultType,
  PreviousYearStatus,
} from 'Containers/PreviousYears/types';
import { routes } from 'Constants/routes';
import { stages } from 'Constants/stages';
import { TAX_YEAR } from 'Config';
import { SeasonFiltered } from 'Types/common';

const TAX_YEAR_2019 = '2019';

export const routesByPreviousYearStage = {
  [PreviousYearStatus.CALCULATING]: routes.DATAGATHERING_LOADER,
  [PreviousYearStatus.SUBMITTED]: routes.PREVIOUS_YEARS_WORKING,
  [PreviousYearStatus.READY]: routes.PREVIOUS_YEARS,
};

export const getTextColor = (
  taxYear2019Recoverable: boolean,
  needEfirma: boolean,
  isInProgress: boolean
): keyof typeof textColors => {
  return taxYear2019Recoverable || needEfirma || isInProgress ? 'dark' : 'fresh';
};

export const preDaUserStages = [
  stages.NOT_INITIALIZED,
  stages.DATA_PARTIAL_PULLED,
  stages.DATA_ERROR,
  stages.DATA_RETRIEVAL,
  stages.DEDUCTIONS,
  stages.PERSONAL_DATA,
  stages.JOBS,
  stages.INVESTMENTS,
  stages.RESULT,
];

export const isPreDaUserOrIsCalculating = (
  stage?: string,
  data?: PueblaPreviousYearsResponse
): boolean => {
  const isCalculating = data?.stage === PreviousYearStatus.CALCULATING;
  return (
    (!!stage && preDaUserStages.includes(stage) && isCalculating) ||
    (!!stage && !preDaUserStages.includes(stage) && isCalculating)
  );
};

export const renderCalculatingInfo = (
  isAnalyze: boolean,
  isRequest?: boolean,
  data?: PueblaPreviousYearsResponse
): boolean => {
  const isPending =
    data &&
    data?.records?.find(item => item?.lastStatus === PreviousYearSatStatus.NOT_AVAILABLE) &&
    data.stage !== PreviousYearStatus.SUBMITTED &&
    !data?.records?.some(item => item.isRecoverable);
  return (isAnalyze && isRequest) || (isRequest && !data) || (isRequest && isPending) || false;
};

export const isUserInFlowUserOrIsZero = (
  stage?: string,
  data?: PueblaPreviousYearsResponse,
  isRequest?: boolean
): boolean => {
  const notIsCalculating = data?.stage !== PreviousYearStatus.CALCULATING;
  const isZero = data?.refundStatusType === PreviousYearsResultType.ZERO;
  if (isRequest) {
    return !!stage && !preDaUserStages.includes(stage) && isZero && notIsCalculating;
  }
  return !!stage && preDaUserStages.includes(stage) && isZero && notIsCalculating;
};

export const handleRedirectUser = (
  data?: PueblaPreviousYearsResponse,
  isRequest?: RequestPreviousYearResponse
): void => {
  const isRequested = isRequest?.isRequested;
  const isTaxYearMatch = isRequest?.taxYear === TAX_YEAR;
  const isCalculatingStage = data?.stage === PreviousYearStatus.CALCULATING;
  const isRefundToReturn = data?.refundStatusType === PreviousYearsResultType.TO_RETURN;

  if (!isRequested) {
    history.push(routes.PREVIOUS_YEARS_INFO);
  } else if (isTaxYearMatch && !data) {
    history.push(routes.DATAGATHERING_LOADER, {
      products: {
        isPreviousYear: true,
      },
    });
  } else if (isCalculatingStage && isRefundToReturn) {
    history.push(routes.PREVIOUS_YEARS_CALCULATING_LOADER);
  } else {
    history.push(
      data && data?.stage ? routesByPreviousYearStage[data.stage] : routes.PREVIOUS_YEARS_INFO,
      {
        products: {
          isPreviousYear: true,
        },
      }
    );
  }
};

export const getAmountToRecoverage = (data?: PueblaPreviousYearsResponse): number => {
  return (
    data?.records?.reduce(
      (acc, record) => acc + (record.isRecoverable ? Math.abs(record.amountTotal || 0) : 0),
      0
    ) || 0
  );
};

export const isTaxYear2019Recoverable = (data?: PueblaPreviousYearsResponse): boolean => {
  return (
    data?.records?.some(
      record =>
        record &&
        record?.taxYear === TAX_YEAR_2019 &&
        record?.isRecoverable &&
        data?.stage !== PreviousYearStatus.SUBMITTED
    ) || false
  );
};

export const isReadyAndNotZero = (data?: PueblaPreviousYearsResponse): boolean => {
  return (
    data?.stage === PreviousYearStatus.READY &&
    data?.refundStatusType !== PreviousYearsResultType.ZERO
  );
};

export const needEfirma = (
  satUserData?: SatResponseType,
  isRequest?: boolean,
  data?: PueblaPreviousYearsResponse
): boolean => {
  return (
    (!satUserData?.efirma &&
      !satUserData?.efirmaIsValid &&
      isRequest &&
      data?.stage === PreviousYearStatus.SUBMITTED) ||
    false
  );
};
export const getUserHaveEfirma = (satUserData?: SatResponseType): boolean => {
  return (satUserData?.efirma && satUserData?.efirmaIsValid) || false;
};

export const getIfUserAAisMaior = (data?: PueblaPreviousYearsResponse): boolean => {
  const isMaiorAA =
    Math.abs(data?.amountPending ?? 0) > Math.abs(data?.compensatedTaxReturns?.[0]?.amount ?? 0);
  return isMaiorAA;
};

export const shouldRenderBanner = (
  tax?: SeasonFiltered,
  satUserData?: SatResponseType,
  data?: PueblaPreviousYearsResponse,
  isUserWithPreFiling?: boolean
): boolean => {
  if (data?.compensatedTaxReturns) return !getIfUserAAisMaior(data);

  if (!data) return true;

  if (isUserWithPreFiling) return false;

  const { stage, refundStatusType } = data;

  const stageMap = {
    [PreviousYearStatus.CALCULATING]: true,
    [PreviousYearStatus.SUBMITTED]: getUserHaveEfirma(satUserData),
  };

  if (stage in stageMap) return stageMap[stage];
  if (refundStatusType === PreviousYearsResultType.ZERO) return true;

  return Boolean(tax?.stage && preDaUserStages.includes(tax.stage));
};

export const getModeByPreviousYearStatus = (
  renderCalculatingBanner?: boolean,
  renderZeroBanner?: boolean,
  renderPreviousYearContent?: boolean,
  needUserEfirma?: boolean,
  is2019Recoverable?: boolean,
  isSubmitted?: boolean
): WrapModes => {
  if (renderCalculatingBanner) return WrapModes.soft;
  if (renderZeroBanner) return WrapModes.cold_gradient;
  if (renderPreviousYearContent) {
    if (isSubmitted && !needUserEfirma) return WrapModes.cold;
    if (needUserEfirma) return WrapModes.warning;
    if (is2019Recoverable) return WrapModes.danger;
    return WrapModes.success;
  }
  return WrapModes.cold_gradient;
};

export const shouldRenderZeroBanner = (
  userStage?: string,
  isRequest?: boolean,
  data?: PueblaPreviousYearsResponse,
  isSubmitted?: boolean,
  isUserWithPreFiling?: boolean
): boolean => {
  const isUserInFlowOrIsZero = isUserInFlowUserOrIsZero(data?.stage, data, isRequest);
  const isPending =
    data &&
    data?.records?.find(item => item?.lastStatus === PreviousYearSatStatus.NOT_AVAILABLE) &&
    !data?.records?.some(item => item.isRecoverable);
  return Boolean(
    (userStage &&
      preDaUserStages.includes(userStage) &&
      !isUserWithPreFiling &&
      !isPending &&
      isRequest &&
      data?.stage === PreviousYearStatus.READY) ||
      (isUserInFlowOrIsZero && data && isRequest && !isSubmitted && !isPending)
  );
};

export const shouldRenderPreviousYearContent = (
  userStage?: string,
  isRequest?: boolean,
  data?: PueblaPreviousYearsResponse,
  isSubmitted?: boolean,
  isUserWithPreFiling?: boolean
): boolean => {
  return Boolean(
    isRequest &&
      ((data &&
        isReadyAndNotZero(data) &&
        userStage &&
        (!preDaUserStages.includes(userStage) || isUserWithPreFiling)) ||
        isSubmitted)
  );
};
